import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Shield, ArrowLeft, User, Mail, Phone, Building, FileText, KeyRound, Loader2, Wrench, Download, LayoutList } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ViewServiceAgreement } from '@/components/portal/ViewServiceAgreement';
import { MfaSettings } from '@/components/portal/MfaSettings';
import { Helmet } from '@/components/Helmet';
import { LogoShield } from '@/components/LogoShield';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { usePortfolioPricingApplication } from '@/hooks/use-portfolio-pricing-application';
import { PortfolioPricingStatusCard } from '@/components/portal/PortfolioPricingStatusCard';
import {
  ACCOUNT_USAGE_OPTIONS,
  normalizeAccountUsagePath,
  type AccountUsagePath,
} from '@/lib/portfolioPricing';
import {
  REVIEWED_PRICING_SCHEDULE_TITLE,
  SERVICE_AGREEMENT_VERSION,
  downloadServiceAgreementPdf,
  downloadReviewedPricingSchedulePdf,
} from '@/lib/serviceAgreement';

export default function Account() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { checking } = useRequireMfa();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [runningBillingGraceCron, setRunningBillingGraceCron] = useState(false);
  const [billingGraceResult, setBillingGraceResult] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [usagePath, setUsagePath] = useState<AccountUsagePath>('own_site');
  const getErrorMessage = (err: unknown) => {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    if (typeof err === 'object' && err && 'message' in err) {
      const msg = (err as { message?: unknown }).message;
      return typeof msg === 'string' ? msg : '';
    }
    return '';
  };

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });


  const handleResetPassword = async () => {
    if (!user?.email) return;
    
    setIsResettingPassword(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/portal/auth`,
      });
      
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (err: unknown) {
      toast.error('Failed to send password reset email');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user!.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: portfolioPricingApplication, isLoading: portfolioPricingApplicationLoading } =
    usePortfolioPricingApplication(user?.id, Boolean(user?.id));

  const { data: latestReviewedPricingSchedule } = useQuery({
    queryKey: ['reviewed-pricing-schedule', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviewed_pricing_schedule_acceptances')
        .select('*')
        .eq('user_id', user!.id)
        .order('accepted_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setPhone(profile.phone || '');
      setCompanyName(profile.company_name || '');
    }

    if (user) {
      if (!profile) {
        console.warn('[account] No persisted profile row found; falling back to auth metadata for display.');
      }
      if (!profile?.full_name) {
        setFullName(typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : '');
      }
      if (!profile) {
        setPhone('');
      }
      if (!profile?.company_name) {
        setCompanyName(typeof user.user_metadata?.company_name === 'string' ? user.user_metadata.company_name : '');
      }
      setUsagePath(normalizeAccountUsagePath(
        typeof user.user_metadata?.qualification_path === 'string'
          ? user.user_metadata.qualification_path
          : null,
      ));
    }
  }, [profile, user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user!.id,
          email: user!.email || null,
          full_name: fullName,
          phone: phone,
          company_name: companyName,
        }, {
          onConflict: 'user_id',
        });

      if (error) throw error;
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          company_name: companyName,
          qualification_path: usagePath,
        },
      });

      if (authError) throw authError;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['profile', user!.id] }),
        queryClient.invalidateQueries({ queryKey: ['profile-payment-bypass', user!.id] }),
      ]);
      toast.success('Account details updated successfully');
    } catch (err: unknown) {
      toast.error('Failed to update account details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunBillingGraceCron = async () => {
    setRunningBillingGraceCron(true);
    setBillingGraceResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('billing-grace-cron');
      if (error) throw error;
      const pausedCount = typeof data?.paused === 'number' ? data.paused : null;
      const success = data?.success === true;
      const message = pausedCount !== null
        ? `Billing grace check complete. Paused ${pausedCount} site(s).`
        : success
          ? 'Billing grace check complete.'
          : 'Billing grace check finished.';
      setBillingGraceResult(message);
      toast.success(message);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Unable to run billing grace check.';
      setBillingGraceResult(errorMessage);
      toast.error(errorMessage);
    } finally {
      setRunningBillingGraceCron(false);
    }
  };

  if (checking || loading || !user || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Account Settings | ShadowScan Portal</title>
          <meta
            name="description"
            content="Update your ShadowScan portal profile, contact details, MFA preferences, and service agreement access to keep your security account current."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Account Settings | ShadowScan Portal</title>
        <meta
          name="description"
          content="Manage your ShadowScan portal account: edit contact info, company details, MFA settings, and access your security service agreement."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Account Settings</span>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        <Card className="cyber-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Account Details
            </CardTitle>
            <CardDescription>
              Manage your account information and contact details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="bg-muted/50 font-mono"
                />
                <p className="text-xs text-muted-foreground">Contact support to change your email address</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullName" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Smith"
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+61 400 000 000"
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  Company Name
                </Label>
                <Input
                  id="company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company Pty Ltd"
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4">
                <div className="space-y-1">
                  <Label className="flex items-center gap-2">
                    <LayoutList className="h-4 w-4 text-primary" />
                    How will you use ShadowScan?
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    This updates your account preference for portal guidance. It does not change your pricing path or approval status.
                  </p>
                </div>
                <RadioGroup
                  value={usagePath}
                  onValueChange={(value) => setUsagePath(value as AccountUsagePath)}
                  className="space-y-3"
                >
                  {ACCOUNT_USAGE_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      htmlFor={`account-usage-${option.value}`}
                      className="flex cursor-pointer items-start gap-3 rounded-md border border-border/60 bg-background/60 p-3 transition hover:border-primary/30"
                    >
                      <RadioGroupItem
                        value={option.value}
                        id={`account-usage-${option.value}`}
                        className="mt-0.5"
                      />
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-foreground">{option.label}</div>
                        <div className="text-xs leading-relaxed text-muted-foreground">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="pt-4 border-t border-border">
                <Button type="submit" className="w-full" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="cyber-card mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" />
              Portfolio Pricing Status
            </CardTitle>
            <CardDescription>
              Review your current pricing-path status. Full application details and actions stay in Billing &amp; subscriptions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {portfolioPricingApplicationLoading ? (
              <div className="text-sm text-muted-foreground">Loading portfolio pricing status…</div>
            ) : portfolioPricingApplication ? (
              <>
                <PortfolioPricingStatusCard
                  application={portfolioPricingApplication}
                  compact
                  activateDomainHref="/portal/add-domain"
                  reviewedPricingSchedule={latestReviewedPricingSchedule}
                />
                <div className="text-sm text-muted-foreground">
                  Submitted via {portfolioPricingApplication.submission_source}
                  {portfolioPricingApplication.business_name
                    ? ` · ${portfolioPricingApplication.business_name}`
                    : ''}
                </div>
                <Button variant="outline" onClick={() => navigate('/portal/billing')}>
                  View portfolio pricing details
                </Button>
              </>
            ) : (
              <>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                  You&apos;re currently on the standard self-serve path. If you manage multiple client or portfolio sites, you can apply for reviewed portfolio pricing from Billing &amp; subscriptions.
                </div>
                <Button variant="outline" onClick={() => navigate('/portal/billing')}>
                  Open Billing &amp; subscriptions
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="cyber-card mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Security
            </CardTitle>
            <CardDescription>
              Manage your account security settings.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div>
                <p className="font-medium">Reset Password</p>
                <p className="text-sm text-muted-foreground">Send a password reset link to your email</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleResetPassword}
                disabled={isResettingPassword}
                className="w-full sm:w-auto"
              >
                {isResettingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* MFA Settings */}
        <div className="mt-6">
          <MfaSettings
            userId={user.id}
            userEmail={user.email || ''}
            currentMethod={profile?.mfa_method || null}
            onMethodChange={() => queryClient.invalidateQueries({ queryKey: ['profile', user.id] })}
          />
        </div>

        {/* Legal Section */}
        <Card className="cyber-card mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Legal
            </CardTitle>
            <CardDescription>
              View your service agreement and terms.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div>
                <p className="font-medium leading-tight">Service Agreement</p>
                <p className="text-sm text-muted-foreground leading-tight">
                  {profile?.service_agreement_accepted_at
                    ? `Accepted version ${profile.service_agreement_version || SERVICE_AGREEMENT_VERSION} on ${new Date(profile.service_agreement_accepted_at).toLocaleString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}`
                    : 'View the current ShadowScan Security Services Agreement'}
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    downloadServiceAgreementPdf({
                      clientName: fullName,
                      clientEmail: user.email || '',
                      acceptedAt: profile?.service_agreement_accepted_at,
                      version: profile?.service_agreement_version || SERVICE_AGREEMENT_VERSION,
                    })
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF copy
                </Button>
                <ViewServiceAgreement />
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-muted/20 rounded-lg border border-border/50">
              <div>
                <p className="font-medium leading-tight flex items-center gap-2">
                  <LayoutList className="h-4 w-4 text-primary" />
                  {REVIEWED_PRICING_SCHEDULE_TITLE}
                </p>
                <p className="text-sm text-muted-foreground leading-tight">
                  {latestReviewedPricingSchedule
                    ? `Accepted ${new Date(latestReviewedPricingSchedule.accepted_at).toLocaleString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })} · ${new Intl.NumberFormat('en-AU', {
                        style: 'currency',
                        currency: (latestReviewedPricingSchedule.dev_price_currency || 'aud').toUpperCase(),
                      }).format(latestReviewedPricingSchedule.dev_price_cents / 100)} per site · ${latestReviewedPricingSchedule.quantity} domain${latestReviewedPricingSchedule.quantity === 1 ? '' : 's'}`
                    : profile?.account_kind === 'dev'
                      ? 'Your reviewed pricing schedule will appear here after you accept dev pricing in Add Domains.'
                      : 'Reviewed pricing schedules appear here only for approved dev accounts after activation.'}
                </p>
                {latestReviewedPricingSchedule && (
                  <p className="text-xs text-muted-foreground mt-1 leading-tight">
                    No removal fee applies. No partial-month refund is issued for the current billing period. Future billing for a removed reviewed-pricing domain stops from the next billing cycle.
                    {typeof latestReviewedPricingSchedule.minimum_active_domains === 'number'
                      ? ` Reviewed pricing may be reassessed if active paid domains remain below ${latestReviewedPricingSchedule.minimum_active_domains} for one full billing cycle.`
                      : ''}
                  </p>
                )}
              </div>
              {latestReviewedPricingSchedule && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    downloadReviewedPricingSchedulePdf({
                      clientName: fullName,
                      clientEmail: user.email || '',
                      acceptedAt: latestReviewedPricingSchedule.accepted_at,
                      serviceAgreementVersion: latestReviewedPricingSchedule.service_agreement_version,
                      pricingScheduleVersion: latestReviewedPricingSchedule.pricing_schedule_version,
                      planName: latestReviewedPricingSchedule.plan_name,
                      devPriceCents: latestReviewedPricingSchedule.dev_price_cents,
                      devPriceCurrency: latestReviewedPricingSchedule.dev_price_currency,
                      quantity: latestReviewedPricingSchedule.quantity,
                      monthlyTotalCents: latestReviewedPricingSchedule.monthly_total_cents,
                      chargeTodayCents: latestReviewedPricingSchedule.charge_today_cents,
                      estimatedFirstChargeCents: latestReviewedPricingSchedule.estimated_first_charge_cents,
                      trialApplied: latestReviewedPricingSchedule.trial_applied,
                      trialDays: latestReviewedPricingSchedule.trial_days,
                      billingStartsAt: latestReviewedPricingSchedule.billing_starts_at,
                      minimumActiveDomains: latestReviewedPricingSchedule.minimum_active_domains,
                    })
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download reviewed pricing schedule
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Admin Tools - Only visible to admins */}
        {isAdmin && (
          <Card className="cyber-card mt-6 border-accent/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-accent">
                <Wrench className="h-5 w-5" />
                Admin Tools
              </CardTitle>
              <CardDescription>
                Administrative functions for system maintenance.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-3">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={runningBillingGraceCron}
                    >
                      {runningBillingGraceCron ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Running billing grace check...
                        </>
                      ) : (
                        'Run billing grace check'
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Run billing grace check?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will evaluate billing grace periods and pause auto-updates for domains past the 7-day grace window.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={runningBillingGraceCron}>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRunBillingGraceCron} disabled={runningBillingGraceCron}>
                        Run now
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async () => {
                    Sentry.captureException(new Error('Sentry test error'));
                    const flushed = await Sentry.flush(2000);
                    if (flushed) {
                      toast.success('Sentry test event sent.');
                    } else {
                      toast.error('Sentry test event not confirmed. Check DSN and network.');
                    }
                  }}
                >
                  Throw Sentry Error
                </Button>
                {billingGraceResult && (
                  <span className="text-xs text-muted-foreground">{billingGraceResult}</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <CurrencyDock />
    </div>
  );
}
